/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import { columns as checkerDetailscolumns } from '../../components/CheckerDetailsTable/CheckerDetailsTableStructure';
import { getFilteredCheckerDetailsColumns, getReasonStatusMapping } from '../commonUtil';
import { DEFAULT_OFFSET, FIN_STATUS_MAPPER_FOR_API } from '../constants';
import { getFromLocalStore } from './generalMethods';

export const uncheckAllOptions: (optionsObject: any) => any = (optionsObject) => {
  return Object.fromEntries(Object.keys(optionsObject).map((key) => [key, false]));
};

export const restructureDataForClf: (tableData: Array<any>, filterKey: string) => { [key: string]: boolean } = (tableData, filterKey) => {
  const values = Array.from(new Set(
    tableData
      .flatMap((item: any) => item[filterKey] ?? [])
      .flat()
      .filter((item: any): item is string => typeof item === 'string' && item.length > 0),
  ));

  // If filterKey is 'fin_description', ensure the static key is present and placed on top
  let finalValues = values;
  if (filterKey === 'fin_description') {
    if (!values.includes('None / Multiple values')) {
      finalValues = ['None / Multiple values', ...values];
    }
  }

  if (filterKey === 'fin_status') {
    // Map using FIN_STATUS_MAPPER_FOR_UI and then reorder as specified
    // const mapped = finalValues.map((status: string) => FIN_STATUS_MAPPER_FOR_UI[status] || status);

    // Desired order
    // const ordered = desiredOrder.filter((item) => mapped.includes(item));
    // const rest = mapped.filter((item) => !desiredOrder.includes(item));
    // finalValues = [...ordered, ...rest];
    finalValues = ['Wrong Part', 'To be Analysed', 'Match', 'Not Checked'];
  }
  if (filterKey === 'edited') {
    finalValues = ['Yes', 'No'];
  }
  const valuesAsObject = finalValues.reduce((acc: any, key: any) => {
    acc[key] = false;
    return acc;
  }, {} as Record<string, boolean>);

  return valuesAsObject;
};

export const extractFilterSelected: (obj: Record<string, Record<string, boolean>>) => Record<string, Array<string>> = (obj) => {
  const result: Record<string, Array<string>> = {};
  Object.entries(obj).forEach(([key, value]) => {
    result[key] = Object.entries(value).filter(([, v]) => v === true).map(([k]) => k);
  });
  return result;
};

export const updateCheckedForClf: (clfState: Record<string, boolean>, checkedValues: Array<string>) => Record<string, boolean> = (clfState, checkedValues) => {
  if (checkedValues.length === 0) {
    return Object.fromEntries(Object.keys(clfState).map((key) => [key, false]));
  }
  return Object.fromEntries(
    Object.keys(clfState).map((key) => [key, checkedValues.includes(key)]),
  );
};

export const createConditionsObjForChecker = (tableFilters: Record<string, Array<string>>) => {
  const { reasonMapping, statusMapping } = getReasonStatusMapping();
  const finalEntries = Object.entries(tableFilters).flatMap(([key, value]) => {
    if (key === 'reason') {
      return [[key, value.map((item: string) => reasonMapping[item])]];
    }
    if (key === 'source_status') {
      return [[key, value.map((item: string) => statusMapping[item])]];
    }
    if (key === 'fleet_fin' && value.includes('No FIN')) {
      // 1. Create the new ['no_fin', ['true']] entry
      const noFinEntry = ['no_fin', ['true']];

      // 2. Filter 'No FIN' out of the original value array
      const filteredFins = value.filter((item) => item !== 'No FIN');

      // Return both entries. We must also handle the case where
      // the filter leaves an empty array for 'fleet_fin'.
      if (filteredFins.length > 0) {
        // If other FINs remain, return both the flag and the filtered list
        const fleetFinEntry = [key, filteredFins];
        return [noFinEntry, fleetFinEntry];
      }
      // If no other FINs remain, just return the flag
      return [noFinEntry];
    }
    if ((key === 'ata' || key === 'fin_description') && value.includes('None / Multiple values')) {
      const noneMultipleEntry = [key, ['NULL', 'ZZZZ']];
      const filteredValues = value.filter((item) => item !== 'None / Multiple values');
      if (filteredValues.length > 0) {
        return [[key, ['NULL', 'ZZZZ', ...filteredValues]]];
      }
      return [noneMultipleEntry];
    }
    if (key === 'fin_status') {
      return [[key, value.map((status: string) => FIN_STATUS_MAPPER_FOR_API[status] || status)]];
    }
    if (key === 'pnr') {
      return [[key, value.map((item: string) => item.replace(/, /g, '_NEXT_'))]];
    }
    return [[key, value]];
  });
  return Object.fromEntries(finalEntries.filter((entry) => entry !== undefined));
};

type checkerApiPayloadType = {
  operator_code_icao: any[];
  conditions: { [key: string]: any };
  columns: string[];
  orderBy?: string[];
  sortType?: string;
}

export const createPayloadObjForChecker: (
  selectedTableFilters: Record<string, Array<string>>,
  checkerDetailFiltersSelected: checkerFilters,
  fleet_detail_filter_selected: checkerFilters,
  sortColumn: string,
  sortOrder: string,
  featureToggle: boolean
) => checkerApiPayloadType = (
  selectedTableFilters,
  checkerDetailFiltersSelected,
  fleet_detail_filter_selected,
  sortColumn,
  sortOrder,
  featureToggle,
) => {
  const apiConditions = createConditionsObjForChecker(selectedTableFilters);
  const cleanedTableFilters = Object.fromEntries(
    Object.entries(apiConditions).filter(
      ([, v]) => !Array.isArray(v) || v.length > 0,
    ),
  );
  const filteredCheckerDetailscolumns = getFilteredCheckerDetailsColumns(checkerDetailscolumns);
  const payload: checkerApiPayloadType = {
    operator_code_icao: new Array(checkerDetailFiltersSelected.operator_name),
    conditions: {
      id_aircraft_registration: fleet_detail_filter_selected.selected_msn,
      ...cleanedTableFilters,
    },
    columns: featureToggle ? checkerDetailscolumns : filteredCheckerDetailscolumns,
  };
  if (sortColumn && sortOrder) {
    payload.orderBy = [`${sortColumn}`];
    payload.sortType = sortOrder;
  }
  return payload;
};

type sbApiPayloadType = {
  operator_code_icao: Array<string>;
  offset: number;
  limit: number;
  isSbStatusToggle?: boolean;
  isConcurrentToggle?: boolean;
  conditions: Record<string, Array<string>>;
  orderBy?: string[];
  sortType?: string;
}

export const createPayloadObjForSB: (
  selectedTableFilters: Record<string, Array<string>>,
  sbConfigurations: Record<string, Array<string> | string>,
  concurrentToggle: boolean,
  sbStatusViewToggle: boolean,
  sortColumn: string,
  sortOrder: string
) => sbApiPayloadType = (
  selectedTableFilters,
  sbConfigurations,
  concurrentToggle,
  sbStatusViewToggle,
  sortColumn,
  sortOrder,
) => {
  const cleanedTableFilters = Object.fromEntries(
    Object.entries(selectedTableFilters).filter(
      ([, v]) => !Array.isArray(v) || v.length > 0,
    ),
  );
  const payload: sbApiPayloadType = {
    operator_code_icao: [sbConfigurations.operator_name as string],
    offset: Number(DEFAULT_OFFSET),
    limit: Number(getFromLocalStore('apiPaginationSize')),
    isSbStatusToggle: sbStatusViewToggle,
    isConcurrentToggle: concurrentToggle,
    conditions: {
      aircraft_type: sbConfigurations.aircraft_type as string[],
      id_aircraft_registration: sbConfigurations.selected_msn as string[],
      ...cleanedTableFilters,
    },
  };
  if (sortColumn && sortOrder) {
    payload.orderBy = [`${sortColumn}`];
    payload.sortType = sortOrder;
  }
  return payload;
};

type SbRecord = {
  [key: string]: string | null;
};

export const convertSbRecordsToFilterObj = (records: SbRecord[]): Record<string, Record<string, boolean>> => {
  const result: Record<string, Record<string, boolean>> = {};

  records.forEach((record) => {
    Object.entries(record).forEach(([key, value]) => {
      const val = value ?? 'null';
      if (!result[key]) {
        result[key] = {};
      }
      result[key][val] = false;
    });
  });

  return result;
};

type ReloadSBTableDataParams = {
  selectedTableFilters: Record<string, Array<string>>;
  currentTableFilters: Record<string, Array<string>>;
  sortColumn: string;
  sortOrder: string;
  browserDispatcher: any;
  popupFilterState: any;
  sbdataTableState: any;
  setSelectedSbClf: any;
  loadSbBrowseData: any;
};

export const reloadSBtableData = ({
  selectedTableFilters,
  currentTableFilters,
  sortColumn,
  sortOrder,
  browserDispatcher,
  popupFilterState,
  sbdataTableState,
  setSelectedSbClf,
  loadSbBrowseData,
}: ReloadSBTableDataParams) => {
  if (!_.isEqual(selectedTableFilters, currentTableFilters)) {
    browserDispatcher(
      setSelectedSbClf({
        filterObject: selectedTableFilters,
      }),
    );

    const payload = createPayloadObjForSB(
      selectedTableFilters,
      (popupFilterState.sb_filter_selection_configuration ?? {}) as Record<
        string,
        Array<string> | string
      >,
      !!sbdataTableState.concurrentToggle,
      !!sbdataTableState.sbStatusViewToggle,
      sortColumn,
      sortOrder,
    );

    browserDispatcher(loadSbBrowseData(payload)());
  }
};

export const formatPartNumber = (part: any) => {
  if (!Array.isArray(part)) return part;
  const tokens = _.flatMap(part, (pn: any) => String(pn).split(',').map((p: string) => p.trim()).filter(Boolean));
  const unique = _.uniq(tokens);
  return unique.join(', ');
};

export const loadMsnOptions: (
  fleetDetailFilterOptions: fleetFilterOptionsTypes,
  fleet_detail_filter_selected: checkerFilters,
) => { [key: string]: boolean } = (
  fleetDetailFilterOptions,
  fleet_detail_filter_selected,
) => {
  const msnArr = fleetDetailFilterOptions.id_aircraft_registration as Array<string>;
  const selectedMsns = fleet_detail_filter_selected.selected_msn as Array<string>;
  let checked_msns: string[];

  if (selectedMsns?.length) {
    checked_msns = selectedMsns;
  } else {
    checked_msns = msnArr;
  }

  const msnsAsObject = checked_msns.reduce(
    (acc: Record<string, boolean>, key: string) => {
      acc[key] = false;
      return acc;
    },
    {},
  );

  return msnsAsObject;
};
